// Third-party packages
import {
	capitalize,
} from '@modules/text';
import i18n, {
	InterpolationOptions,
} from 'i18next';
import parsePhoneNumber from 'libphonenumber-js';
import {
	initReactI18next,
} from 'react-i18next';

// Relative imports
import {
	getIndefiniteArticle,
} from './getIndefiniteArticle';

// import Backend from 'i18next-http-backend'; // REMOVED - not needed
// import LanguageDetector from 'i18next-browser-languagedetector';
// not like to use this?
// have a look at the Quick start guide
// for passing in lng and translations on init

// LAZY LOAD TRANSLATIONS FOR BETTER PERFORMANCE
// Avoid blocking main thread with large JSON parsing
let local_gb: Record<string, unknown> = {};
let local_fr: Record<string, unknown> = {};

const loadTranslations = async () => {
	const [
		frModule,
		gbModule,
	] = await Promise.all([
		import('@localizations/fr-FR.json'),
		import('@localizations/en-GB.json'),
	]);
	local_fr = frModule.default;
	local_gb = gbModule.default;

	// Update i18n with loaded translations
	i18n.addResourceBundle('fr-FR', 'translation', local_fr, true, true);
	i18n.addResourceBundle('en-GB', 'translation', local_gb, true, true);
};

interface InterpolationOptionsProps extends InterpolationOptions {
	decimals?: number;
}

// Start with empty translations to avoid blocking
// They will be loaded asynchronously
export const resources = {
	'en-GB': {
		translation: {},
	},
	'fr-FR': {
		translation: {},
	},
} as const;
export const defaultNS = 'fr-FR';

i18n
	// DISABLED HTTP BACKEND FOR PERFORMANCE - using lazy loaded JSON imports
	// .use(Backend)
	// detect user language
	// learn more: https://github.com/i18next/i18next-browser-languageDetector
	// .use(LanguageDetector)
	// pass the i18n instance to react-i18next.
	.use(initReactI18next)
	// init i18next
	// for all options read: https://www.i18next.com/overview/configuration-options
	.init({
		debug: false,
		fallbackLng: 'fr-FR',
		interpolation: {
			escapeValue: false, // not needed for react as it escapes by default
			format: (

				value: unknown,
				rawFormat: string,
				lng: string,
				options: InterpolationOptionsProps,
			) => {
				const [
					format,
					...additionalValues
				] = rawFormat.split(',').map((v) => v.trim());

				switch (format) {
					case 'capitalize': {
						return capitalize(value);
					}
					case 'indefinite': {
						return getIndefiniteArticle(value);
					}
					case 'lowercase': {
						return value.toLowerCase();
					}
					case 'number': {
						interface FormatConf {
							style: string;
							maximumFractionDigits: number;
							minimumFractionDigits: number;
							currency?: string;
						}

						const formatConf = {
							maximumFractionDigits: 0,
							minimumFractionDigits: 0,
							style: 'decimal',
						} as FormatConf;

						// Check if currency iso 4217 code is ok
						const availableCurrencyAdditionalValues = [
							'EUR',
							'GBP',
						];
						const hasCurrencyCode = additionalValues[0] && availableCurrencyAdditionalValues.includes(additionalValues[0]) ? additionalValues[0] : false;
						if (hasCurrencyCode) {
							formatConf.style = 'currency';
							formatConf.currency = hasCurrencyCode; // currency code must be ISO 4217 compliant. Ex : EUR, GBP, ...
						}

						// Option to add decimals to number
						if (options && options.decimals) {
							formatConf.maximumFractionDigits = options.decimals;
							formatConf.minimumFractionDigits = options.decimals;
						}
						return Intl.NumberFormat(lng, formatConf as Intl.NumberFormatOptions).format(value);
					}
					case 'tel': {
						const phoneNumber = parsePhoneNumber(value, 'FR');
						const formatedPhoneNUmber = phoneNumber ? phoneNumber.formatNational() : null;
						return formatedPhoneNUmber;
					}
					case 'uppercase': {
						return value.toUpperCase();
					}
				}
				return value;
			},
		},
		lng: localStorage.getItem('i18nextLng') ? /* istanbul ignore next */ localStorage.getItem('i18nextLng') : 'fr-FR', // keep it. We will decide to remove it if we don't use the localStorage when the authentication will be done
		load: 'currentOnly',
		react: {
			useSuspense: false, // CRITICAL: Disable Suspense to prevent blocking
		},
		resources,
		supportedLngs: [
			'fr-FR',
			'en-GB',
		],
		// detectLngFromHeaders: false,
		// compatibilityJSON: 'v3', // for plural
	});

export default i18n;

// Load translations asynchronously after initial render
// This prevents blocking the main thread with 194KB of JSON parsing
setTimeout(() => {
	loadTranslations().catch(error => {
		console.error('❌ Failed to load translations:', error);
	});
}, 100);

export function getI18nKeyOrFallback(possibleKeys: string[], fallbackKey: string): string {
	for (const key of possibleKeys) {
		if (i18n.exists(key)) {
			return key;
		}
	}
	return fallbackKey;
}
